import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api, { type BsvDeviceTransferSession } from 'api/api'
import styles from 'components/modals/device-transfer-modal.module.scss'
import {
    DEVICE_TRANSFER_SESSION_EVENT,
    clearPendingDeviceTransferSession,
    getPendingDeviceTransferSessions,
    type DeviceTransferSessionEventDetail,
} from 'state/channels/useUserChannel'
import {
    clearClientShare,
    getClientShare,
    getOrCreateLocalDeviceId,
    isSelfCustodyReinitializationRequiredError,
    persistSelfCustodyWalletForDevice,
    recoverLocalWalletTextWithShares,
    type ClientShareRecord,
} from 'lib/bsv-signing'
import {
    decryptWalletPayload,
    deriveDeviceTransferSessionMaterial,
    encryptWalletPayload,
    computeDeviceTransferSasProof,
    generateDeviceTransferKeyPair,
    verifyDeviceTransferSasProof,
    type DeviceTransferKeyPair,
    type DeviceTransferRole,
    type DeviceTransferTranscript,
} from 'lib/bsv-device-transfer'

// Scope local consent to one accept attempt (session id + approver key), so a reset and
// re-accept of the same session never inherits an earlier confirmation.
function confirmationScope(session: {
    id: string
    approver_ephemeral_public_key: string | null
}): string {
    return `${session.id}:${session.approver_ephemeral_public_key || ''}`
}

function transcriptForSession(session: {
    id: string
    requesting_device_id: string
    approving_device_id: string | null
    requester_ephemeral_public_key: string
    approver_ephemeral_public_key: string | null
}): DeviceTransferTranscript {
    return {
        sessionId: session.id,
        requestingDeviceId: session.requesting_device_id,
        approvingDeviceId: session.approving_device_id || '',
        requesterEphemeralPublicKey: session.requester_ephemeral_public_key,
        approverEphemeralPublicKey: session.approver_ephemeral_public_key || '',
    }
}

type DeviceTransferModalProps = {
    modalParams?: {
        role?: DeviceTransferRole
        autoStart?: boolean
        sessionId?: string | null
        initialSession?: BsvDeviceTransferSession | null
        launchSource?: 'wallet' | 'realtime' | string
        [key: string]: unknown
    }
    closeModal?: () => void
}

const DEVICE_TRANSFER_TERMINAL_STATUSES = new Set(['completed', 'cancelled', 'expired'])

const isTerminalDeviceTransferSession = (session: BsvDeviceTransferSession | null): boolean =>
    Boolean(session && DEVICE_TRANSFER_TERMINAL_STATUSES.has(session.status))

export default function DeviceTransferModal({ modalParams, closeModal }: DeviceTransferModalProps) {
    const currentUserId = gon?.currentUser?.id
    const initialRole = modalParams?.role || null

    const [localDeviceId, setLocalDeviceId] = useState('')
    const [clientShareRecord, setClientShareRecord] = useState<ClientShareRecord | null>(null)
    const [trackedDeviceTransferId, setTrackedDeviceTransferId] = useState<string | null>(
        modalParams?.sessionId || modalParams?.initialSession?.id || null,
    )
    const [initialDeviceTransferSession, setInitialDeviceTransferSession] =
        useState<BsvDeviceTransferSession | null>(modalParams?.initialSession || null)
    const [knownDeviceTransferSessions, setKnownDeviceTransferSessions] = useState<
        BsvDeviceTransferSession[]
    >([])
    const [activeDeviceTransferId, setActiveDeviceTransferId] = useState<string | null>(
        modalParams?.role === 'requester' ? modalParams?.sessionId || null : null,
    )
    const [activeDeviceTransferRole, setActiveDeviceTransferRole] =
        useState<DeviceTransferRole | null>(initialRole)
    const [activeDeviceTransferKeyPair, setActiveDeviceTransferKeyPair] =
        useState<DeviceTransferKeyPair | null>(null)
    const [activeDeviceTransferSasNumbers, setActiveDeviceTransferSasNumbers] = useState<
        [number, number, number] | null
    >(null)
    const [activeDeviceTransferPayloadKey, setActiveDeviceTransferPayloadKey] =
        useState<CryptoKey | null>(null)
    const [activeDeviceTransferConfirmKey, setActiveDeviceTransferConfirmKey] =
        useState<CryptoKey | null>(null)
    // Session id whose numbers THIS device's user explicitly confirmed in this modal instance.
    // Never derived from server flags: the server (or anyone holding the login token) can set those.
    const [locallyConfirmedSessionId, setLocallyConfirmedSessionId] = useState<string | null>(null)
    const [deviceTransferBusy, setDeviceTransferBusy] = useState(false)
    const [deviceTransferError, setDeviceTransferError] = useState('')
    const [deviceTransferSuccess, setDeviceTransferSuccess] = useState('')
    const uploadedDeviceTransferIdsRef = useRef<Set<string>>(new Set())
    const restoredDeviceTransferIdsRef = useRef<Set<string>>(new Set())
    const activeDeviceTransferSessionRef = useRef<BsvDeviceTransferSession | null>(null)
    const activeDeviceTransferRoleRef = useRef<DeviceTransferRole | null>(null)
    const localDeviceIdRef = useRef('')
    const clientShareDeviceIdRef = useRef<string | null>(null)
    const modalMountedRef = useRef(true)
    const requesterLaunchStartedRef = useRef(false)

    const activeDeviceTransferSession = useMemo(
        () =>
            knownDeviceTransferSessions.find(session => session.id === activeDeviceTransferId) ||
            null,
        [activeDeviceTransferId, knownDeviceTransferSessions],
    )

    const localDeviceTransferConfirmed = useMemo(() => {
        const session = activeDeviceTransferSession || initialDeviceTransferSession
        if (!session || !activeDeviceTransferRole) {
            return false
        }

        if (activeDeviceTransferRole === 'requester') {
            return session.requester_sas_confirmed
        }

        return (
            session.approver_sas_confirmed &&
            locallyConfirmedSessionId === confirmationScope(session)
        )
    }, [
        activeDeviceTransferRole,
        activeDeviceTransferSession,
        initialDeviceTransferSession,
        locallyConfirmedSessionId,
    ])
    const bothSidesConfirmed = Boolean(
        (activeDeviceTransferSession || initialDeviceTransferSession)?.requester_sas_confirmed &&
            (activeDeviceTransferSession || initialDeviceTransferSession)?.approver_sas_confirmed,
    )

    const incomingApprovalRequest = useMemo(() => {
        if (!trackedDeviceTransferId) {
            return activeDeviceTransferSession || initialDeviceTransferSession
        }

        return (
            knownDeviceTransferSessions.find(session => session.id === trackedDeviceTransferId) ||
            (initialDeviceTransferSession?.id === trackedDeviceTransferId
                ? initialDeviceTransferSession
                : null)
        )
    }, [
        activeDeviceTransferSession,
        initialDeviceTransferSession,
        knownDeviceTransferSessions,
        trackedDeviceTransferId,
    ])

    const upsertDeviceTransferSession = useCallback((session: BsvDeviceTransferSession) => {
        setInitialDeviceTransferSession(previousSession =>
            previousSession?.id === session.id ? session : previousSession,
        )
        setKnownDeviceTransferSessions(previousSessions => {
            const remainingSessions = previousSessions.filter(
                previousSession => previousSession.id !== session.id,
            )

            if (DEVICE_TRANSFER_TERMINAL_STATUSES.has(session.status)) {
                return remainingSessions
            }

            return [...remainingSessions, session]
        })
    }, [])

    const clearActiveDeviceTransfer = useCallback(() => {
        setTrackedDeviceTransferId(null)
        setInitialDeviceTransferSession(null)
        setActiveDeviceTransferId(null)
        setActiveDeviceTransferRole(null)
        setActiveDeviceTransferKeyPair(null)
        setActiveDeviceTransferSasNumbers(null)
        setActiveDeviceTransferPayloadKey(null)
        setActiveDeviceTransferConfirmKey(null)
        setLocallyConfirmedSessionId(null)
        setDeviceTransferBusy(false)
        setDeviceTransferError('')
    }, [])

    const refreshLocalDeviceState = useCallback(async () => {
        if (!currentUserId) {
            return
        }

        const record = await getClientShare(currentUserId)
        setClientShareRecord(record)
        const existingLocalDeviceId =
            record?.deviceId || (await getOrCreateLocalDeviceId(currentUserId))
        setLocalDeviceId(existingLocalDeviceId)
    }, [currentUserId])

    const startRequesterTransfer = useCallback(async () => {
        if (!currentUserId || !localDeviceId) {
            return
        }

        try {
            setDeviceTransferBusy(true)
            setDeviceTransferError('')
            setDeviceTransferSuccess(
                'Waiting for one of your trusted devices to approve this setup.',
            )

            const keyPair = await generateDeviceTransferKeyPair()
            const createResult = await api.createBsvDeviceTransferSession(
                localDeviceId,
                keyPair.publicKeyBase64Url,
            )

            if (createResult.error || !createResult.session) {
                throw new Error(createResult.error || 'Failed to create device transfer session')
            }

            requesterLaunchStartedRef.current = true
            setTrackedDeviceTransferId(createResult.session.id)
            setInitialDeviceTransferSession(createResult.session)
            setActiveDeviceTransferId(createResult.session.id)
            setActiveDeviceTransferRole('requester')
            setActiveDeviceTransferKeyPair(keyPair)
            upsertDeviceTransferSession(createResult.session)
        } catch (error) {
            setDeviceTransferError(
                error instanceof Error ? error.message : 'Failed to start device transfer',
            )
        } finally {
            setDeviceTransferBusy(false)
        }
    }, [currentUserId, localDeviceId, upsertDeviceTransferSession])

    const handleAcceptDeviceTransfer = useCallback(
        async (session: BsvDeviceTransferSession) => {
            const approvingDeviceId = clientShareRecord?.deviceId || ''
            if (!approvingDeviceId) {
                return
            }

            try {
                setDeviceTransferBusy(true)
                setDeviceTransferError('')
                const keyPair = await generateDeviceTransferKeyPair()
                const acceptResult = await api.acceptBsvDeviceTransferSession(
                    session.id,
                    approvingDeviceId,
                    keyPair.publicKeyBase64Url,
                )

                if (acceptResult.session) {
                    upsertDeviceTransferSession(acceptResult.session)
                }

                if (acceptResult.error || !acceptResult.session) {
                    throw new Error(acceptResult.error || 'Failed to accept device transfer')
                }

                setTrackedDeviceTransferId(acceptResult.session.id)
                setInitialDeviceTransferSession(acceptResult.session)
                setActiveDeviceTransferId(acceptResult.session.id)
                setActiveDeviceTransferRole('approver')
                setActiveDeviceTransferKeyPair(keyPair)
            } catch (error) {
                setDeviceTransferError(
                    error instanceof Error ? error.message : 'Failed to accept device transfer',
                )
            } finally {
                setDeviceTransferBusy(false)
            }
        },
        [clientShareRecord?.deviceId, upsertDeviceTransferSession],
    )

    const handleConfirmDeviceTransferSas = useCallback(async () => {
        if (!activeDeviceTransferSession || !activeDeviceTransferRole) {
            return
        }

        const deviceId =
            activeDeviceTransferRole === 'requester'
                ? localDeviceId
                : clientShareRecord?.deviceId || ''

        if (!deviceId || !activeDeviceTransferSasNumbers || !activeDeviceTransferConfirmKey) {
            return
        }

        try {
            setDeviceTransferBusy(true)
            setDeviceTransferError('')
            const sasProof = await computeDeviceTransferSasProof(
                activeDeviceTransferConfirmKey,
                activeDeviceTransferRole,
                activeDeviceTransferSasNumbers,
                transcriptForSession(activeDeviceTransferSession),
            )
            const confirmResult = await api.confirmBsvDeviceTransferSessionSas(
                activeDeviceTransferSession.id,
                deviceId,
                sasProof,
            )

            if (confirmResult.session) {
                upsertDeviceTransferSession(confirmResult.session)
            }

            if (confirmResult.error || !confirmResult.session) {
                throw new Error(confirmResult.error || 'Failed to confirm matching numbers')
            }

            setLocallyConfirmedSessionId(confirmationScope(activeDeviceTransferSession))
        } catch (error) {
            setDeviceTransferError(
                error instanceof Error ? error.message : 'Failed to confirm matching numbers',
            )
        } finally {
            setDeviceTransferBusy(false)
        }
    }, [
        activeDeviceTransferConfirmKey,
        activeDeviceTransferRole,
        activeDeviceTransferSasNumbers,
        activeDeviceTransferSession,
        clientShareRecord?.deviceId,
        localDeviceId,
        upsertDeviceTransferSession,
    ])

    const handleCancelActiveDeviceTransfer = useCallback(async () => {
        if (!activeDeviceTransferSession || !activeDeviceTransferRole) {
            closeModal?.()
            return
        }

        const deviceId =
            activeDeviceTransferRole === 'requester'
                ? localDeviceId
                : clientShareRecord?.deviceId || ''

        if (!deviceId) {
            return
        }

        try {
            setDeviceTransferBusy(true)
            setDeviceTransferError('')
            const cancelResult =
                activeDeviceTransferRole === 'approver'
                    ? await api.releaseBsvDeviceTransferSession(
                          activeDeviceTransferSession.id,
                          deviceId,
                      )
                    : await api.cancelBsvDeviceTransferSession(
                          activeDeviceTransferSession.id,
                          deviceId,
                      )

            if (cancelResult.session) {
                upsertDeviceTransferSession(cancelResult.session)
            }

            if (cancelResult.error) {
                throw new Error(cancelResult.error)
            }

            if (activeDeviceTransferRole === 'approver') {
                setDeviceTransferSuccess(
                    'This device stepped back. The new device is waiting for another trusted device.',
                )
            } else {
                setDeviceTransferSuccess('This transfer request was cancelled.')
            }
            clearActiveDeviceTransfer()
            closeModal?.()
        } catch (error) {
            setDeviceTransferError(
                error instanceof Error ? error.message : 'Failed to cancel device transfer',
            )
        } finally {
            setDeviceTransferBusy(false)
        }
    }, [
        activeDeviceTransferRole,
        activeDeviceTransferSession,
        clearActiveDeviceTransfer,
        clientShareRecord?.deviceId,
        closeModal,
        localDeviceId,
        upsertDeviceTransferSession,
    ])

    const closeIntroModal = useCallback(() => {
        setDeviceTransferError('')
        closeModal?.()
    }, [closeModal])

    const deviceTransferStatusMessage = useMemo(() => {
        const displaySession = activeDeviceTransferSession || incomingApprovalRequest
        if (!displaySession || !activeDeviceTransferRole) {
            return ''
        }

        if (activeDeviceTransferRole === 'requester' && displaySession.status === 'pending') {
            return 'Ask another device you already trust to approve this request, then keep this window open.'
        }

        if (displaySession.status === 'accepted' && !activeDeviceTransferSasNumbers) {
            return 'Getting the security check ready on both devices.'
        }

        if (!localDeviceTransferConfirmed && activeDeviceTransferSasNumbers) {
            return 'Look at the three numbers on both devices. Only continue if every number matches.'
        }

        if (localDeviceTransferConfirmed && !bothSidesConfirmed) {
            return 'This device is confirmed. Approve the matching numbers on the other device.'
        }

        if (
            activeDeviceTransferRole === 'approver' &&
            bothSidesConfirmed &&
            !displaySession.payload_ready
        ) {
            return 'The numbers matched. Sending the wallet to the new device now.'
        }

        if (
            activeDeviceTransferRole === 'requester' &&
            bothSidesConfirmed &&
            displaySession.payload_ready
        ) {
            return 'The wallet arrived safely. Finishing setup on this device now.'
        }

        if (activeDeviceTransferRole === 'requester' && bothSidesConfirmed) {
            return 'The trusted device is sending your wallet securely.'
        }

        if (activeDeviceTransferRole === 'requester') {
            return 'Waiting for the trusted device to join the security check.'
        }

        return 'Waiting for the new device to finish connecting.'
    }, [
        bothSidesConfirmed,
        activeDeviceTransferRole,
        activeDeviceTransferSasNumbers,
        activeDeviceTransferSession,
        incomingApprovalRequest,
        localDeviceTransferConfirmed,
    ])

    useEffect(() => {
        modalMountedRef.current = true

        return () => {
            modalMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        setKnownDeviceTransferSessions(getPendingDeviceTransferSessions())
        refreshLocalDeviceState().catch(error => {
            setDeviceTransferError(
                error instanceof Error ? error.message : 'Failed to load local device state',
            )
        })
    }, [refreshLocalDeviceState])

    useEffect(() => {
        if (modalParams?.role) {
            setActiveDeviceTransferRole(modalParams.role)
        }

        if (modalParams?.initialSession) {
            setInitialDeviceTransferSession(modalParams.initialSession)
            upsertDeviceTransferSession(modalParams.initialSession)
        }

        if (modalParams?.sessionId || modalParams?.initialSession?.id) {
            setTrackedDeviceTransferId(
                modalParams?.sessionId || modalParams?.initialSession?.id || null,
            )
        }
    }, [
        modalParams?.initialSession,
        modalParams?.role,
        modalParams?.sessionId,
        upsertDeviceTransferSession,
    ])

    useEffect(() => {
        if (
            modalParams?.role !== 'requester' ||
            !modalParams?.autoStart ||
            requesterLaunchStartedRef.current ||
            activeDeviceTransferId ||
            !localDeviceId
        ) {
            return
        }

        startRequesterTransfer().catch(() => undefined)
    }, [
        activeDeviceTransferId,
        localDeviceId,
        modalParams?.autoStart,
        modalParams?.role,
        startRequesterTransfer,
    ])

    useEffect(() => {
        const handleDeviceTransferEvent = (event: Event) => {
            const detail = (event as CustomEvent<DeviceTransferSessionEventDetail>).detail
            const deviceTransferSession = detail.deviceTransferSession
            upsertDeviceTransferSession(deviceTransferSession)
            if (trackedDeviceTransferId && deviceTransferSession.id === trackedDeviceTransferId) {
                setInitialDeviceTransferSession(deviceTransferSession)
            }

            if (
                detail.eventType === 'accepted' &&
                activeDeviceTransferRole === 'approver' &&
                trackedDeviceTransferId === deviceTransferSession.id &&
                clientShareRecord?.deviceId &&
                !activeDeviceTransferKeyPair &&
                deviceTransferSession.approving_device_id !== clientShareRecord.deviceId
            ) {
                clearActiveDeviceTransfer()
                setDeviceTransferError('Another trusted device accepted this request first.')
                closeModal?.()
            }

            if (
                detail.eventType === 'released' &&
                activeDeviceTransferRole === 'approver' &&
                trackedDeviceTransferId === deviceTransferSession.id
            ) {
                clearActiveDeviceTransfer()
                setDeviceTransferSuccess(
                    'This request went back to waiting for another trusted device.',
                )
                closeModal?.()
            }

            if (
                detail.eventType === 'cancelled' &&
                trackedDeviceTransferId === deviceTransferSession.id
            ) {
                clearActiveDeviceTransfer()
                closeModal?.()
            }

            if (
                detail.eventType === 'completed' &&
                trackedDeviceTransferId === deviceTransferSession.id
            ) {
                clearActiveDeviceTransfer()
                if (activeDeviceTransferRole === 'approver') {
                    setDeviceTransferSuccess(
                        'This new device is connected now. You can close this window.',
                    )
                }
            }
        }

        window.addEventListener(DEVICE_TRANSFER_SESSION_EVENT, handleDeviceTransferEvent)
        return () => {
            window.removeEventListener(DEVICE_TRANSFER_SESSION_EVENT, handleDeviceTransferEvent)
        }
    }, [
        activeDeviceTransferRole,
        activeDeviceTransferKeyPair,
        clearActiveDeviceTransfer,
        clientShareRecord?.deviceId,
        closeModal,
        trackedDeviceTransferId,
        upsertDeviceTransferSession,
    ])

    useEffect(() => {
        if (!trackedDeviceTransferId) {
            return
        }

        const trackedDeviceTransferSession = activeDeviceTransferSession || incomingApprovalRequest

        if (isTerminalDeviceTransferSession(trackedDeviceTransferSession)) {
            clearPendingDeviceTransferSession(trackedDeviceTransferId)
            if (trackedDeviceTransferSession?.status === 'completed') {
                return
            }

            clearActiveDeviceTransfer()
            return
        }

        let cancelled = false
        const intervalId = window.setInterval(async () => {
            const result = await api.getBsvDeviceTransferSession(trackedDeviceTransferId)
            if (cancelled) {
                return
            }

            if (!result.session) {
                if (result.error) {
                    if (!incomingApprovalRequest) {
                        clearActiveDeviceTransfer()
                        setDeviceTransferError(result.error)
                    }
                }
                return
            }

            upsertDeviceTransferSession(result.session)
        }, 5000)

        return () => {
            cancelled = true
            window.clearInterval(intervalId)
        }
    }, [
        activeDeviceTransferSession,
        clearActiveDeviceTransfer,
        incomingApprovalRequest,
        trackedDeviceTransferId,
        upsertDeviceTransferSession,
    ])

    useEffect(() => {
        if (!activeDeviceTransferSession || !activeDeviceTransferKeyPair) {
            setActiveDeviceTransferSasNumbers(null)
            setActiveDeviceTransferPayloadKey(null)
            setActiveDeviceTransferConfirmKey(null)
            return
        }

        if (
            !activeDeviceTransferSession.approving_device_id ||
            !activeDeviceTransferSession.approver_ephemeral_public_key
        ) {
            setActiveDeviceTransferSasNumbers(null)
            setActiveDeviceTransferPayloadKey(null)
            setActiveDeviceTransferConfirmKey(null)
            return
        }

        const remotePublicKey =
            activeDeviceTransferRole === 'approver'
                ? activeDeviceTransferSession.requester_ephemeral_public_key
                : activeDeviceTransferSession.approver_ephemeral_public_key

        let cancelled = false
        ;(async () => {
            try {
                const sessionMaterial = await deriveDeviceTransferSessionMaterial(
                    activeDeviceTransferKeyPair.privateKey,
                    remotePublicKey,
                    transcriptForSession(activeDeviceTransferSession),
                )

                if (cancelled) {
                    return
                }

                setActiveDeviceTransferSasNumbers(sessionMaterial.sasNumbers)
                setActiveDeviceTransferPayloadKey(sessionMaterial.payloadKey)
                setActiveDeviceTransferConfirmKey(sessionMaterial.confirmKey)
            } catch (error) {
                if (cancelled) {
                    return
                }

                setDeviceTransferError(
                    error instanceof Error ? error.message : 'Failed to derive security numbers',
                )
            }
        })()

        return () => {
            cancelled = true
        }
    }, [activeDeviceTransferKeyPair, activeDeviceTransferRole, activeDeviceTransferSession])

    useEffect(() => {
        if (
            activeDeviceTransferRole !== 'approver' ||
            !activeDeviceTransferSession ||
            !activeDeviceTransferPayloadKey ||
            !activeDeviceTransferConfirmKey ||
            !activeDeviceTransferSasNumbers ||
            locallyConfirmedSessionId !== confirmationScope(activeDeviceTransferSession) ||
            !activeDeviceTransferSession.requester_sas_proof ||
            activeDeviceTransferSession.payload_ready ||
            uploadedDeviceTransferIdsRef.current.has(activeDeviceTransferSession.id)
        ) {
            return
        }

        uploadedDeviceTransferIdsRef.current.add(activeDeviceTransferSession.id)
        let cancelled = false

        ;(async () => {
            let shouldAllowRetry = true
            try {
                setDeviceTransferBusy(true)
                setDeviceTransferError('')
                setDeviceTransferSuccess('Sending your wallet securely to the new device.')

                // Require a requester confirmation that only the holder of the requesting
                // device's ephemeral key could have produced; server flags are not trusted.
                const requesterProofValid = await verifyDeviceTransferSasProof(
                    activeDeviceTransferConfirmKey,
                    'requester',
                    activeDeviceTransferSasNumbers,
                    transcriptForSession(activeDeviceTransferSession),
                    activeDeviceTransferSession.requester_sas_proof,
                )
                if (!requesterProofValid) {
                    shouldAllowRetry = false
                    throw new Error(
                        'The new device could not prove it saw the same numbers. Cancel and start the transfer again.',
                    )
                }

                const approvingDeviceId = clientShareRecord?.deviceId || ''
                if (!currentUserId || !approvingDeviceId || !clientShareRecord?.clientShare) {
                    shouldAllowRetry = false
                    throw new Error(
                        'This device is missing its local self-custody share state. Re-set up self-custody on this browser before transferring again.',
                    )
                }

                const serverShare = await api.getBsvServerShare(currentUserId, approvingDeviceId)
                if (!serverShare?.treechatShare) {
                    shouldAllowRetry = false
                    throw new Error(
                        'This device is missing its matching server share. Re-set up self-custody on this browser before transferring again.',
                    )
                }

                const walletText = await recoverLocalWalletTextWithShares(
                    currentUserId,
                    serverShare.treechatShare,
                    clientShareRecord.clientShare,
                )
                const envelope = await encryptWalletPayload(
                    walletText,
                    activeDeviceTransferPayloadKey,
                )
                const uploadResult = await api.uploadBsvDeviceTransferPayload(
                    activeDeviceTransferSession.id,
                    clientShareRecord?.deviceId || '',
                    envelope.encryptedWalletPayload,
                    envelope.encryptedWalletNonce,
                )

                if (uploadResult.session) {
                    upsertDeviceTransferSession(uploadResult.session)
                }

                if (uploadResult.error) {
                    throw new Error(uploadResult.error)
                }

                if (cancelled || !uploadResult.session) {
                    return
                }

                setDeviceTransferSuccess(
                    'The wallet was shared securely. The new device is finishing setup now.',
                )
            } catch (error) {
                if (isSelfCustodyReinitializationRequiredError(error)) {
                    shouldAllowRetry = false
                }
                if (shouldAllowRetry) {
                    uploadedDeviceTransferIdsRef.current.delete(activeDeviceTransferSession.id)
                }
                if (cancelled) {
                    return
                }

                setDeviceTransferError(
                    error instanceof Error
                        ? error.message
                        : 'Failed to send the secure wallet package',
                )
            } finally {
                if (!cancelled) {
                    setDeviceTransferBusy(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [
        activeDeviceTransferConfirmKey,
        activeDeviceTransferPayloadKey,
        activeDeviceTransferRole,
        activeDeviceTransferSasNumbers,
        activeDeviceTransferSession,
        clientShareRecord?.clientShare,
        clientShareRecord?.deviceId,
        currentUserId,
        locallyConfirmedSessionId,
        upsertDeviceTransferSession,
    ])

    useEffect(() => {
        if (
            activeDeviceTransferRole !== 'requester' ||
            !activeDeviceTransferSession ||
            !activeDeviceTransferPayloadKey ||
            !activeDeviceTransferSession.payload_ready ||
            restoredDeviceTransferIdsRef.current.has(activeDeviceTransferSession.id)
        ) {
            return
        }

        restoredDeviceTransferIdsRef.current.add(activeDeviceTransferSession.id)
        let cancelled = false

        ;(async () => {
            try {
                setDeviceTransferBusy(true)
                setDeviceTransferError('')
                const payloadResult = await api.getBsvDeviceTransferPayload(
                    activeDeviceTransferSession.id,
                    localDeviceId,
                )

                if (!payloadResult.payload) {
                    throw new Error(payloadResult.error || 'Failed to download the secure package')
                }

                const walletText = await decryptWalletPayload(
                    payloadResult.payload.encrypted_wallet_payload,
                    payloadResult.payload.encrypted_wallet_nonce,
                    activeDeviceTransferPayloadKey,
                )
                if (!currentUserId) {
                    throw new Error('Missing current user id for wallet restore')
                }
                const persistedWallet = await persistSelfCustodyWalletForDevice(
                    currentUserId,
                    localDeviceId,
                    walletText,
                )

                const registerResult = await api.registerBsvServerShare(
                    localDeviceId,
                    persistedWallet.shares.treechatShare,
                    persistedWallet.walletIdentity,
                )

                if (!registerResult.success) {
                    throw new Error(registerResult.error || 'Failed to register server share')
                }

                const activateResult = await api.activateBsvDeviceTransferSession(
                    activeDeviceTransferSession.id,
                    localDeviceId,
                )

                clearPendingDeviceTransferSession(activeDeviceTransferSession.id)
                if (activateResult.error || !activateResult.session) {
                    throw new Error(activateResult.error)
                }

                if (!modalMountedRef.current) {
                    return
                }

                await refreshLocalDeviceState()
                clearActiveDeviceTransfer()
                setDeviceTransferSuccess(
                    'This device is now connected to your wallet. Treechat will refresh once to finish setting it up here.',
                )
                window.alert(
                    'This device is now connected to your wallet. Treechat will refresh once to finish setting it up here.',
                )
                window.location.reload()
            } catch (error) {
                restoredDeviceTransferIdsRef.current.delete(activeDeviceTransferSession.id)
                if (currentUserId) {
                    await clearClientShare(currentUserId)
                }

                if (cancelled) {
                    return
                }

                setDeviceTransferError(
                    error instanceof Error
                        ? error.message
                        : 'Failed to finish setup on this device',
                )
            } finally {
                if (!cancelled) {
                    setDeviceTransferBusy(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [
        activeDeviceTransferPayloadKey,
        activeDeviceTransferRole,
        activeDeviceTransferSession,
        clearActiveDeviceTransfer,
        currentUserId,
        localDeviceId,
        refreshLocalDeviceState,
    ])

    useEffect(() => {
        activeDeviceTransferSessionRef.current = activeDeviceTransferSession
        activeDeviceTransferRoleRef.current = activeDeviceTransferRole
    }, [activeDeviceTransferRole, activeDeviceTransferSession])

    useEffect(() => {
        localDeviceIdRef.current = localDeviceId
        clientShareDeviceIdRef.current = clientShareRecord?.deviceId || null
    }, [clientShareRecord?.deviceId, localDeviceId])

    useEffect(() => {
        return () => {
            const session = activeDeviceTransferSessionRef.current
            const role = activeDeviceTransferRoleRef.current

            if (!session || !role || isTerminalDeviceTransferSession(session)) {
                return
            }

            const deviceId =
                role === 'requester' ? localDeviceIdRef.current : clientShareDeviceIdRef.current
            if (!deviceId) {
                return
            }

            if (role === 'approver') {
                if (session.approving_device_id !== deviceId) {
                    return
                }

                api.releaseBsvDeviceTransferSession(session.id, deviceId)
                return
            }

            api.cancelBsvDeviceTransferSession(session.id, deviceId)
        }
    }, [])

    const showApprovalIntro =
        activeDeviceTransferRole === 'approver' &&
        Boolean(incomingApprovalRequest) &&
        !activeDeviceTransferKeyPair &&
        incomingApprovalRequest?.status === 'pending'
    const showApproverBootstrapCard =
        activeDeviceTransferRole === 'approver' &&
        Boolean(trackedDeviceTransferId) &&
        !showApprovalIntro &&
        !activeDeviceTransferSession
    const showRequesterBootstrapCard =
        activeDeviceTransferRole === 'requester' &&
        !activeDeviceTransferSession &&
        (!trackedDeviceTransferId || Boolean(modalParams?.autoStart))

    const showVerification =
        Boolean(activeDeviceTransferSession) && Boolean(activeDeviceTransferSasNumbers)
    const showActiveTransferCard = Boolean(activeDeviceTransferSession) && !showApprovalIntro
    const showVerificationDecision = showVerification && !localDeviceTransferConfirmed
    const activeStepLabel = showVerification
        ? 'Compare the numbers'
        : bothSidesConfirmed
        ? 'Finishing setup'
        : 'Secure transfer'
    const cancelButtonLabel = showVerificationDecision
        ? 'Numbers Do Not Match'
        : activeDeviceTransferRole === 'approver'
        ? 'Stop Sharing'
        : 'Cancel Setup'

    return (
        <div className={styles.container}>
            <div className={styles.shell}>
                <div className={styles.header}>
                    <div className={styles.eyebrow}>
                        {activeDeviceTransferRole === 'approver'
                            ? 'Trusted device'
                            : activeDeviceTransferRole === 'requester'
                            ? 'New device'
                            : 'Device transfer'}
                    </div>
                    <h1 className={styles.title}>Secure Device Transfer</h1>
                    <p className={styles.description}>
                        Use another trusted device to connect this one to your wallet without
                        exposing the wallet contents to Treechat.
                    </p>
                </div>

                {deviceTransferError && (
                    <div className={styles.errorBanner}>{deviceTransferError}</div>
                )}
                {deviceTransferSuccess && (
                    <div className={styles.successBanner}>{deviceTransferSuccess}</div>
                )}

                {showApprovalIntro && incomingApprovalRequest && (
                    <div className={styles.card}>
                        <div className={styles.stepLabel}>Incoming request</div>
                        <h2 className={styles.stepTitle}>Approve this new device</h2>
                        <p className={styles.stepBody}>
                            Device <code>{incomingApprovalRequest.requesting_device_id}</code> is
                            asking to connect to this wallet. If you continue, both devices will
                            compare the same three security numbers before anything is shared.
                        </p>
                        <div className={styles.actions}>
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                onClick={closeIntroModal}
                                disabled={deviceTransferBusy}
                            >
                                Not now
                            </button>
                            <button
                                type="button"
                                className={styles.primaryButton}
                                onClick={() => handleAcceptDeviceTransfer(incomingApprovalRequest)}
                                disabled={deviceTransferBusy}
                            >
                                {deviceTransferBusy ? 'Preparing...' : 'Approve'}
                            </button>
                        </div>
                    </div>
                )}

                {showApproverBootstrapCard && (
                    <div className={styles.card}>
                        <div className={styles.stepLabel}>Incoming request</div>
                        <h2 className={styles.stepTitle}>Loading this transfer request</h2>
                        <p className={styles.stepBody}>
                            Stay on this screen for a moment while Treechat finishes opening the
                            approval step for the new device.
                        </p>
                        <div className={styles.actions}>
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                onClick={closeModal}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}

                {showRequesterBootstrapCard && (
                    <div className={styles.card}>
                        <div className={styles.stepLabel}>Starting setup</div>
                        <h2 className={styles.stepTitle}>Waiting for another trusted device</h2>
                        <p className={styles.stepBody}>
                            Treechat is opening a secure transfer request. Keep this window open,
                            then approve the request from a device that already has your wallet.
                        </p>
                        <div className={styles.actions}>
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                onClick={() => handleCancelActiveDeviceTransfer()}
                                disabled={deviceTransferBusy}
                            >
                                Cancel Setup
                            </button>
                        </div>
                    </div>
                )}

                {showActiveTransferCard && activeDeviceTransferSession && (
                    <div
                        className={`${styles.card} ${
                            showVerification ? styles.verificationCard : ''
                        }`}
                    >
                        <div className={styles.stepLabel}>{activeStepLabel}</div>
                        <h2 className={styles.stepTitle}>{deviceTransferStatusMessage}</h2>

                        {showVerification && (
                            <>
                                <div className={styles.numberStageIntro}>
                                    Compare these three numbers on both devices.
                                </div>
                                <div className={styles.numberGrid}>
                                    {activeDeviceTransferSasNumbers?.map(number => (
                                        <div key={number} className={styles.numberPill}>
                                            {number}
                                        </div>
                                    ))}
                                </div>
                                <div className={styles.verificationHint}>
                                    If every number matches, choose <strong>Numbers Match</strong>{' '}
                                    on both devices. If anything is different, stop the transfer.
                                </div>
                            </>
                        )}

                        {!showVerification && (
                            <p className={styles.stepBody}>
                                {activeDeviceTransferRole === 'requester'
                                    ? 'Keep this window open while the trusted device approves and sends your wallet.'
                                    : 'Stay here while the other device approves the matching numbers.'}
                            </p>
                        )}

                        <div className={styles.metaCard}>
                            <div className={styles.metaRow}>
                                <span className={styles.metaLabel}>Status</span>
                                <span className={styles.metaValue}>
                                    {activeDeviceTransferSession.status.replace(/_/g, ' ')}
                                </span>
                            </div>
                            <div className={styles.metaRow}>
                                <span className={styles.metaLabel}>Requesting device</span>
                                <span className={styles.metaValue}>
                                    {activeDeviceTransferSession.requesting_device_id}
                                </span>
                            </div>
                            {activeDeviceTransferSession.approving_device_id && (
                                <div className={styles.metaRow}>
                                    <span className={styles.metaLabel}>Trusted device</span>
                                    <span className={styles.metaValue}>
                                        {activeDeviceTransferSession.approving_device_id}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className={styles.actions}>
                            {!isTerminalDeviceTransferSession(activeDeviceTransferSession) && (
                                <button
                                    type="button"
                                    className={styles.secondaryButton}
                                    disabled={deviceTransferBusy}
                                    onClick={() => handleCancelActiveDeviceTransfer()}
                                >
                                    {cancelButtonLabel}
                                </button>
                            )}
                            {showVerificationDecision && (
                                <button
                                    type="button"
                                    className={styles.primaryButton}
                                    disabled={deviceTransferBusy}
                                    onClick={() => handleConfirmDeviceTransferSas()}
                                >
                                    {deviceTransferBusy ? 'Confirming...' : 'Numbers Match'}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
